import { useState } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { temContexto, toPrompt } from '@domain/agent'
import type { AgentInstructions } from '@domain/agent'
import { Glass } from '@ui/glass/Glass'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { HASH_LEITURA, isLeituraDeAgente } from '@ui/shell/agent-view'
import { Linhas, Remover, Texto, TextoLongo } from './campos'
import { useAgentInstructions } from './use-agent-instructions'
import { Voltar } from '@ui/shell/Voltar'

/**
 * O contexto que os agentes leem antes de caçar preço.
 *
 * ## Por que uma tela, e não um prompt no código
 *
 * *"Prime ativo"*, *"a Americanas do Campo Comprido retira em 2h"*, *"o PIX dá
 * 5%"* — é o que a casa sabe, e vence: o Prime expira, a loja fecha, o cupom de
 * primeira compra é gasto. No código, corrigir isso pediria deploy. Aqui é um
 * campo de texto.
 *
 * ## Uma página, e não abas
 *
 * A tela que serviu de referência usa cinco abas. Aqui é uma rolagem só: no
 * celular, aba esconde campo, e quem preenche isto preenche de uma vez —
 * endereço, sites e cashback são a mesma sentada. Rolar mostra o que falta;
 * aba, não.
 *
 * ## O rascunho é local, e o salvar é explícito
 *
 * Salvar a cada tecla mandaria uma escrita por caractere e deixaria meia frase
 * gravada como contexto. O estado vive aqui até o botão.
 */
/** O alvo de `/ajustes/agente#agentInstructions`. Ver `agent-view.ts`. */
export const SECAO_DO_PROMPT = HASH_LEITURA

export function InstrucoesDeAgente() {
  const estado = useAgentInstructions()
  const leitura = isLeituraDeAgente(useLocation().hash)

  if (estado.status === 'loading') {
    return leitura ? <Cru>Carregando…</Cru> : <Moldura>Carregando…</Moldura>
  }
  if (estado.status === 'failed') {
    const recado = 'Não deu para carregar as instruções. Tente de novo mais tarde.'
    return leitura ? <Cru>{recado}</Cru> : <Moldura>{recado}</Moldura>
  }

  // A página que o agente lê: o markdown e nada mais. Sem cabeçalho, sem
  // botão, sem cartão — cada um deles vira palavra no meio do contexto.
  if (leitura) {
    // Sem checagem de vazio: desde o caminho de volta (decisão 266), esta
    // página nunca é branca — mesmo sem nada configurado ela diz ao agente
    // onde deixar o que encontrar, que é a parte que não depende da casa.
    return <Cru>{toPrompt(estado.instructions)}</Cru>
  }
  // `key` remonta o formulário se a carga trocar — assim o rascunho nasce do
  // que veio do servidor sem um efeito de sincronia.
  return <Formulario key={estado.instructions.updatedAt} carregadas={estado.instructions} />
}

/**
 * O markdown, cru.
 *
 * `<pre>` e não uma renderização de markdown: quem lê é um modelo, e ele quer
 * o `##` e o `-` literais, que são a estrutura. Renderizar transformaria a
 * estrutura em estilo e a apagaria do texto.
 *
 * `whitespace-pre-wrap` porque a alternativa — rolagem horizontal — deixaria
 * linha longa fora da tela de quem abre no celular para conferir.
 */
function Cru({ children }: { children: React.ReactNode }) {
  return <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">{children}</pre>
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <section>
      <Cabecalho />
      <Glass level="frost" className="p-5 text-sm text-[var(--text-muted)]">
        {children}
      </Glass>
    </section>
  )
}

function Cabecalho() {
  return (
    <>
      <div className="mb-2">
        <Voltar para="/ajustes">Ajustes</Voltar>
      </div>
      <h1 className="titulo mb-4 text-2xl">Contexto para agentes</h1>
    </>
  )
}

type Salvamento = 'parado' | 'salvando' | 'salvo' | 'falhou'

function Formulario({ carregadas }: { carregadas: AgentInstructions }) {
  const { agentInstructionsRepository } = useFinanceRepositories()
  const [i, setI] = useState<AgentInstructions>(carregadas)
  const [salvamento, setSalvamento] = useState<Salvamento>('parado')
  const [copiado, setCopiado] = useState(false)

  /** Um patch raso, para os `onChange` não repetirem o spread inteiro. */
  function mude(patch: Partial<AgentInstructions>) {
    setI((atual) => ({ ...atual, ...patch }))
    setSalvamento('parado')
  }

  async function salvar() {
    setSalvamento('salvando')
    try {
      // A limpeza acontece **no salvar**, não a cada tecla: apagar uma linha do
      // meio de um `<textarea>` com a lista já limpa moveria o cursor.
      await agentInstructionsRepository.save(limpe(i))
      setSalvamento('salvo')
    } catch (erro: unknown) {
      console.error('InstrucoesDeAgente: save falhou', erro)
      setSalvamento('falhou')
    }
  }

  const prompt = toPrompt(limpe(i))

  return (
    <section>
      <Cabecalho />

      <p className="mb-4 text-sm text-[var(--text-muted)]">
        O que um agente precisa saber antes de procurar preço para a casa. Nada aqui é obrigatório —
        o que estiver vazio simplesmente não é dito a ele.
      </p>

      <div className="flex flex-col gap-4">
        <Cartao titulo="Objetivo">
          <TextoLongo
            label="O que o agente deve fazer"
            value={i.goal}
            rows={6}
            placeholder="Ex.: encontre o menor custo total considerando preço, frete, cashback e desconto no PIX…"
            onChange={(goal) => {
              mude({ goal })
            }}
          />
        </Cartao>

        <Cartao titulo="Onde eu estou">
          <p className="text-sm text-[var(--text-muted)]">
            Serve para frete, retirada na loja e busca por mercado perto.
          </p>
          <Texto
            label="Rua e número"
            value={i.address.street}
            onChange={(street) => {
              mude({ address: { ...i.address, street } })
            }}
          />
          <Texto
            label="Complemento"
            value={i.address.complement}
            onChange={(complement) => {
              mude({ address: { ...i.address, complement } })
            }}
          />
          <div className="grid grid-cols-2 gap-2">
            <Texto
              label="Bairro"
              value={i.address.district}
              onChange={(district) => {
                mude({ address: { ...i.address, district } })
              }}
            />
            <Texto
              label="CEP"
              value={i.address.cep}
              onChange={(cep) => {
                mude({ address: { ...i.address, cep } })
              }}
            />
            <Texto
              label="Cidade"
              value={i.address.city}
              onChange={(city) => {
                mude({ address: { ...i.address, city } })
              }}
            />
            <Texto
              label="Estado"
              value={i.address.state}
              onChange={(state) => {
                mude({ address: { ...i.address, state } })
              }}
            />
          </div>
          <TextoLongo
            label="Observação sobre busca local"
            value={i.localNote}
            rows={3}
            placeholder="Ex.: sempre pinar o endereço acima antes de buscar — o raio padrão pega o centro."
            onChange={(localNote) => {
              mude({ localNote })
            }}
          />
        </Cartao>

        <Cartao
          titulo="Sites preferidos"
          aoAdicionar={() => {
            mude({
              preferredSites: [
                ...i.preferredSites,
                { host: '', name: '', reasons: [], firstPurchaseUsed: false },
              ],
            })
          }}
        >
          {i.preferredSites.length === 0 ? <Vazio>Nenhum site ainda.</Vazio> : null}
          {i.preferredSites.map((site, n) => (
            <Item
              key={n}
              oQue={site.name === '' ? 'site' : site.name}
              aoRemover={() => {
                mude({ preferredSites: i.preferredSites.filter((_, o) => o !== n) })
              }}
            >
              <div className="grid grid-cols-2 gap-2">
                <Texto
                  label="Nome"
                  value={site.name}
                  placeholder="Amazon"
                  onChange={(name) => {
                    mude({ preferredSites: troque(i.preferredSites, n, { name }) })
                  }}
                />
                <Texto
                  label="Site"
                  value={site.host}
                  placeholder="amazon.com.br"
                  onChange={(host) => {
                    mude({ preferredSites: troque(i.preferredSites, n, { host }) })
                  }}
                />
              </div>
              <Linhas
                label="Por quê (uma por linha)"
                value={site.reasons}
                placeholder={'Prime ativo, frete grátis\nRetirada na loja em 2h'}
                onChange={(reasons) => {
                  mude({ preferredSites: troque(i.preferredSites, n, { reasons }) })
                }}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={site.firstPurchaseUsed}
                  onChange={(e) => {
                    mude({
                      preferredSites: troque(i.preferredSites, n, {
                        firstPurchaseUsed: e.target.checked,
                      }),
                    })
                  }}
                />
                {/* Sem isto o agente propõe um desconto que não existe mais — e
                    o preço que ele achou vira mentira. */}
                Cupom de primeira compra já usado
              </label>
            </Item>
          ))}
        </Cartao>

        <Cartao
          titulo="Cashback"
          aoAdicionar={() => {
            mude({ cashbackSites: [...i.cashbackSites, { name: '', host: '', rate: '' }] })
          }}
        >
          {i.cashbackSites.length === 0 ? <Vazio>Nenhum site de cashback ainda.</Vazio> : null}
          {i.cashbackSites.map((c, n) => (
            <Item
              key={n}
              oQue={c.name === '' ? 'cashback' : c.name}
              aoRemover={() => {
                mude({ cashbackSites: i.cashbackSites.filter((_, o) => o !== n) })
              }}
            >
              <div className="grid grid-cols-2 gap-2">
                <Texto
                  label="Nome"
                  value={c.name}
                  placeholder="Méliuz"
                  onChange={(name) => {
                    mude({ cashbackSites: troque(i.cashbackSites, n, { name }) })
                  }}
                />
                <Texto
                  label="Site"
                  value={c.host}
                  placeholder="meliuz.com.br"
                  onChange={(host) => {
                    mude({ cashbackSites: troque(i.cashbackSites, n, { host }) })
                  }}
                />
              </div>
              <Texto
                label="Quanto volta"
                value={c.rate}
                placeholder="varia por loja (ML 1,5% · Magalu 5%)"
                onChange={(rate) => {
                  mude({ cashbackSites: troque(i.cashbackSites, n, { rate }) })
                }}
              />
            </Item>
          ))}
        </Cartao>

        <Cartao
          titulo="Descontos por forma de pagamento"
          aoAdicionar={() => {
            mude({ paymentDiscounts: [...i.paymentDiscounts, { method: '', note: '' }] })
          }}
        >
          {i.paymentDiscounts.length === 0 ? <Vazio>Nenhum desconto anotado.</Vazio> : null}
          {i.paymentDiscounts.map((p, n) => (
            <Item
              key={n}
              oQue={p.method === '' ? 'desconto' : p.method}
              aoRemover={() => {
                mude({ paymentDiscounts: i.paymentDiscounts.filter((_, o) => o !== n) })
              }}
            >
              <Texto
                label="Forma de pagamento"
                value={p.method}
                placeholder="PIX"
                onChange={(method) => {
                  mude({ paymentDiscounts: troque(i.paymentDiscounts, n, { method }) })
                }}
              />
              <TextoLongo
                label="Observação"
                value={p.note}
                rows={2}
                placeholder="Várias lojas dão 5-10% no PIX. Sempre conferir antes de fechar."
                onChange={(note) => {
                  mude({ paymentDiscounts: troque(i.paymentDiscounts, n, { note }) })
                }}
              />
            </Item>
          ))}
        </Cartao>

        <Cartao
          titulo="Navegação aprendida"
          aoAdicionar={() => {
            mude({
              siteKnowledge: [
                ...i.siteKnowledge,
                { host: '', title: '', baseUrl: '', steps: [], tips: [] },
              ],
            })
          }}
        >
          <p className="text-sm text-[var(--text-muted)]">
            O caminho até o preço num site que dá trabalho — para o próximo agente não redescobrir
            sozinho.
          </p>
          {i.siteKnowledge.map((s, n) => (
            <Item
              key={n}
              oQue={s.title === '' ? 'site' : s.title}
              aoRemover={() => {
                mude({ siteKnowledge: i.siteKnowledge.filter((_, o) => o !== n) })
              }}
            >
              <div className="grid grid-cols-2 gap-2">
                <Texto
                  label="Título"
                  value={s.title}
                  placeholder="Menor Preço (Nota Paraná)"
                  onChange={(title) => {
                    mude({ siteKnowledge: troque(i.siteKnowledge, n, { title }) })
                  }}
                />
                <Texto
                  label="Site"
                  value={s.host}
                  placeholder="menorpreco.notaparana.pr.gov.br"
                  onChange={(host) => {
                    mude({ siteKnowledge: troque(i.siteKnowledge, n, { host }) })
                  }}
                />
              </div>
              <Texto
                label="URL de entrada"
                value={s.baseUrl}
                onChange={(baseUrl) => {
                  mude({ siteKnowledge: troque(i.siteKnowledge, n, { baseUrl }) })
                }}
              />
              <Linhas
                label="Passos (um por linha)"
                value={s.steps}
                rows={4}
                onChange={(steps) => {
                  mude({ siteKnowledge: troque(i.siteKnowledge, n, { steps }) })
                }}
              />
              <Linhas
                label="Dicas (uma por linha)"
                value={s.tips}
                onChange={(tips) => {
                  mude({ siteKnowledge: troque(i.siteKnowledge, n, { tips }) })
                }}
              />
            </Item>
          ))}
        </Cartao>

        {/*
          O que o agente vai ler de fato, montado pela mesma `toPrompt` que uma
          Function usaria. Sem isto a tela seria um formulário que grava JSON
          que ninguém lê — e ninguém saberia se o que digitou virou contexto.
        */}
        <Cartao titulo="O que o agente lê">
          {!temContexto(limpe(i)) ? (
            <Vazio>Ainda não há nada preenchido.</Vazio>
          ) : (
            <>
              {/* Nomeado porque é um bloco de texto longo no meio de um
                  formulário: sem nome, quem usa leitor de tela cai nele sem
                  saber que é a prévia. */}
              <pre
                aria-label="Prévia do que o agente lê"
                className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border border-[var(--border)] p-3 text-xs"
              >
                {prompt}
              </pre>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(prompt).then(
                    () => {
                      setCopiado(true)
                    },
                    (erro: unknown) => {
                      // Copiar pode ser negado (permissão, contexto sem foco).
                      // O texto continua na tela, selecionável — por isso a
                      // falha só vira log, e não um alarme.
                      console.error('InstrucoesDeAgente: copiar falhou', erro)
                    },
                  )
                }}
                className="self-start rounded-lg border border-[var(--border)] px-3 py-1 text-sm"
              >
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </>
          )}
        </Cartao>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            void salvar()
          }}
          disabled={salvamento === 'salvando'}
          className="rounded-[var(--radius-md)] bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {salvamento === 'salvando' ? 'Salvando…' : 'Salvar'}
        </button>
        <span role="status" className="text-sm text-[var(--text-muted)]">
          {salvamento === 'salvo' ? 'Salvo.' : null}
          {salvamento === 'falhou' ? 'Não deu para salvar. Tente de novo.' : null}
        </span>
      </div>

      <p className="mt-6 text-sm">
        <Link to="/ajustes" className="text-[var(--text-muted)] underline">
          Voltar para Ajustes
        </Link>
      </p>
    </section>
  )
}

function Cartao({
  titulo,
  aoAdicionar,
  children,
}: {
  titulo: string
  aoAdicionar?: () => void
  children: React.ReactNode
}) {
  return (
    <Glass level="frost" className="flex flex-col gap-3 p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">{titulo}</h2>
        {aoAdicionar === undefined ? null : (
          <button
            type="button"
            aria-label={`Adicionar em ${titulo}`}
            onClick={aoAdicionar}
            className="rounded-lg border border-[var(--border)] alvo px-2 py-1 text-sm"
          >
            Adicionar
          </button>
        )}
      </div>
      {children}
    </Glass>
  )
}

function Item({
  oQue,
  aoRemover,
  children,
}: {
  oQue: string
  aoRemover: () => void
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--border)] p-3">
      {children}
      <div className="flex justify-end">
        <Remover oQue={oQue} onClick={aoRemover} />
      </div>
    </div>
  )
}

function Vazio({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-[var(--text-muted)]">{children}</p>
}

/** Tira o branco e as linhas vazias que o `<textarea>` deixou passar. */
function linhasDe(v: string[]): string[] {
  return v.map((x) => x.trim()).filter((x) => x !== '')
}

/** Troca um item da lista por ele mesmo com o patch aplicado. */
function troque<T>(lista: T[], n: number, patch: Partial<T>): T[] {
  return lista.map((item, o) => (o === n ? { ...item, ...patch } : item))
}

/**
 * O que vai para o Firestore: sem espaço em volta, sem linha vazia e **sem
 * item em branco** — um cartão adicionado e não preenchido não é contexto.
 */
function limpe(i: AgentInstructions): AgentInstructions {
  return {
    ...i,
    goal: i.goal.trim(),
    localNote: i.localNote.trim(),
    address: {
      street: i.address.street.trim(),
      complement: i.address.complement.trim(),
      district: i.address.district.trim(),
      city: i.address.city.trim(),
      state: i.address.state.trim(),
      cep: i.address.cep.trim(),
    },
    preferredSites: i.preferredSites
      .map((s) => ({
        ...s,
        host: s.host.trim(),
        name: s.name.trim(),
        reasons: linhasDe(s.reasons),
      }))
      .filter((s) => s.host !== '' || s.name !== ''),
    cashbackSites: i.cashbackSites
      .map((c) => ({ name: c.name.trim(), host: c.host.trim(), rate: c.rate.trim() }))
      .filter((c) => c.name !== '' || c.host !== ''),
    paymentDiscounts: i.paymentDiscounts
      .map((p) => ({ method: p.method.trim(), note: p.note.trim() }))
      .filter((p) => p.method !== ''),
    siteKnowledge: i.siteKnowledge
      .map((s) => ({
        host: s.host.trim(),
        title: s.title.trim(),
        baseUrl: s.baseUrl.trim(),
        steps: linhasDe(s.steps),
        tips: linhasDe(s.tips),
      }))
      .filter((s) => s.host !== '' || s.title !== ''),
  }
}
